"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { signUpEmail } from "@/lib/auth-client";
import { acceptRiskDisclosure } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { RiskAgreementModal } from "@/components/RiskAgreementModal";
import { getAuth } from "@/content/auth";
import { PasswordField } from "@/components/auth/PasswordField";
import { clearReferralCode, pendingReferralCode, REFERRAL_CODE_RE, storeReferralCode } from "@/lib/referral-capture";
import { localePath, DEFAULT_LOCALE, type Locale } from "@/lib/i18n";

/**
 * Create an account with email + password (auto-signs-in on success).
 *
 * The trading-risk disclosure is gated HERE, on the route, rather than by whichever page linked
 * in. It used to be raised only by the landing page's "Get started" buttons, which meant every
 * other way in skipped it entirely: /login's "Create one" link, a bookmark, a shared URL, or
 * simply typing /signup. Owning the gate at the destination makes it unbypassable by navigation.
 *
 * Acceptance is RECORDED, not merely displayed: on successful signup the console immediately
 * calls POST /me/risk-disclosure/accept, which stamps users.risk_disclosure_accepted_at and
 * .risk_disclosure_version server-side and writes an audit_log row (migration 0014). The write
 * happens here because the disclosure precedes the account — this is the first moment a row
 * exists to attach it to.
 *
 * This is a SEPARATE document from the Terms of Service (`users.tos_accepted_at` / `tos_version`,
 * GET /me/tos + POST /me/tos/accept), which the gateway enforces before a user can subscribe or
 * connect an exchange key — see components/TosBanner.tsx.
 */
function SignupForm({ locale }: { locale: Locale }) {
  const router = useRouter();
  const params = useSearchParams();
  const a = getAuth(locale);
  const t = a.signup;
  // `?disclosed=1` is set only by the landing page, which just showed this exact modal — it
  // suppresses an immediate duplicate. It is a UX hint, not a security control: forging it skips
  // nothing that matters, because the binding gate is the server-side ToS the gateway enforces
  // before subscribing or connecting an exchange key.
  const [agreed, setAgreed] = useState(params.get("disclosed") === "1");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  // Optional: the inviter's code, prefilled from the link that brought the visitor (`?ref=`).
  const [refCode, setRefCode] = useState(() => (typeof window === "undefined" ? "" : (params.get("ref") ?? pendingReferralCode() ?? "")));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Declining returns to the landing page rather than leaving an unusable form behind.
  if (!agreed) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4">
        <RiskAgreementModal
          locale={locale}
          onAgree={() => setAgreed(true)}
          onClose={() => router.push(localePath(locale, "/"))}
        />
      </main>
    );
  }

  const submit = async () => {
    if (password.length < 12) return setErr(t.errShort);
    if (password !== confirm) return setErr(t.errMismatch);
    setBusy(true); setErr(null);
    // The inviter's code is carried to the first signed-in page, which attaches it (AppShell).
    // An emptied field means no code: the one a link left behind is dropped too.
    if (REFERRAL_CODE_RE.test(refCode)) storeReferralCode(refCode);
    else clearReferralCode();
    const { error } = await signUpEmail(email, password);
    if (error) {
      setBusy(false);
      return setErr(error.message ?? t.errGeneric);
    }

    // Record the disclosure the user just accepted, now that an account (and a session) exists —
    // it is shown pre-account, so this is the earliest point it CAN be written. The server stamps
    // the time and audits it, making the consent provable rather than implied (CLAUDE.md).
    //
    // A failure here does NOT block entry: the account is already created, and stranding the user
    // on a form that cannot succeed would be worse. It is recorded as not-accepted instead, so
    // the existing acceptance gates re-prompt before anything that actually matters — subscribing
    // or connecting an exchange key. Losing the record is the safe direction; fabricating one is
    // the unsafe direction, which is why nothing is written client-side.
    try {
      await acceptRiskDisclosure();
    } catch {
      // Intentionally swallowed — see above. The user is re-prompted at the next gated action.
    }
    setBusy(false);

    // FULL navigation (not router.replace): signup auto-signs-in via better-auth's raw `$fetch`,
    // which sets the cookie but doesn't update the reactive `useSession` store — a soft nav would
    // bounce back to /login. A real load re-inits so `useSession` picks up the new session.
    window.location.replace("/dashboard");
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <div className="panel bracketed w-full max-w-sm space-y-4">
        <div>
          <Logo />
          <p className="mt-3 text-sm text-muted">{t.lead}</p>
        </div>

        <div>
          <label className="label">{t.email}</label>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.emailPlaceholder}
          />
        </div>
        <div>
          <label className="label">{t.password}</label>
          <PasswordField
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            showLabel={a.showPassword}
            hideLabel={a.hidePassword}
          />
        </div>
        <div>
          <label className="label">{t.confirm}</label>
          <PasswordField
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            showLabel={a.showPassword}
            hideLabel={a.hidePassword}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </div>

        <div>
          <label className="label">{t.referral}</label>
          <input
            className="input num"
            placeholder="twokeio-…"
            value={refCode}
            onChange={(e) => setRefCode(e.target.value.toLowerCase().trim())}
          />
        </div>

        <button className="btn w-full" disabled={busy || !email || !password} onClick={submit}>
          {busy ? t.submitting : t.submit}
        </button>

        {err && <p className="text-sm text-bad">{err}</p>}

        <p className="border-t border-border/60 pt-3 text-center text-sm text-muted">
          {t.haveAccount}{" "}
          <Link href={localePath(locale, "/login")} className="text-accent hover:underline">
            {t.signIn}
          </Link>
        </p>
      </div>
    </main>
  );
}

/**
 * `useSearchParams` requires a Suspense boundary in the App Router, so the form lives in its own
 * component and the route export wraps it. The fallback is deliberately inert — it renders no
 * signup form, so there is no state in which the form is reachable before the disclosure has
 * been shown.
 */
export function SignupBody({ locale = DEFAULT_LOCALE }: { locale?: Locale }) {
  return (
    <Suspense
      fallback={
        <main className="flex min-h-screen items-center justify-center px-4">
          <p className="text-sm text-muted">{getAuth(locale).loading}</p>
        </main>
      }
    >
      <SignupForm locale={locale} />
    </Suspense>
  );
}
